import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, ConfirmDialog, EmptyState, Spinner, useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { auth as authApi } from '../../lib/api';
import { isPasskeySupported, createPasskey, suggestPasskeyName } from '../../lib/webauthn';
import { useT } from '../../i18n';

/**
 * My Account card for passkeys: sign in with Touch ID / Face ID / Windows Hello instead of
 * a password. Lists registered passkeys, adds new ones via the browser's WebAuthn prompt,
 * and removes old ones.
 */
export default function PasskeysCard() {
  const t = useT();
  const toast = useToast();
  const [passkeys, setPasskeys] = useState(null);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null); // passkey pending confirm
  const [removeBusy, setRemoveBusy] = useState(false);

  const load = useCallback(() => {
    authApi.listPasskeys().then(setPasskeys).catch(() => setPasskeys([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const addPasskey = async () => {
    setAdding(true);
    try {
      const { sessionId, options } = await authApi.passkeyRegisterOptions();
      const attestation = await createPasskey(options);
      await authApi.registerPasskey(sessionId, suggestPasskeyName(), attestation);
      toast.success(t('Passkey added'), {
        description: t('You can now sign in with your fingerprint, face, or device PIN.'),
      });
      load();
    } catch (err) {
      // The user dismissing the browser prompt is a non-event.
      if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') {
        toast.error(t('Could not add passkey'), { description: err?.message });
      }
    } finally {
      setAdding(false);
    }
  };

  const removePasskey = async () => {
    setRemoveBusy(true);
    try {
      await authApi.deletePasskey(removing.id);
      toast.success(t('Passkey removed'));
      setRemoving(null);
      load();
    } catch (err) {
      toast.error(t('Could not remove passkey'), { description: err?.message });
    } finally {
      setRemoveBusy(false);
    }
  };

  const supported = isPasskeySupported();

  return (
    <Card>
      <Card.Header>
        <Card.Title>{t('Passkeys')}</Card.Title>
        <Card.Subtitle>
          {t('Sign in with Touch ID, Face ID, Windows Hello, or a security key — no password needed.')}
        </Card.Subtitle>
      </Card.Header>
      <Card.Body>
        {passkeys === null ? (
          <Spinner />
        ) : passkeys.length === 0 ? (
          <EmptyState
            icon={<Icon.Fingerprint size={26} />}
            title={t('No passkeys yet')}
            description={
              supported
                ? t('Add a passkey to sign in with your fingerprint, face, or device PIN.')
                : t('This browser does not support passkeys.')
            }
          />
        ) : (
          <ul className="acct-passkeys">
            {passkeys.map((p) => (
              <li className="acct-passkeys__row" key={p.id}>
                <span className="acct-passkeys__icon"><Icon.Fingerprint size={18} /></span>
                <span className="acct-passkeys__info">
                  <span className="acct-passkeys__name">{p.name}</span>
                  <span className="acct-form__muted">
                    {t('Added {date}', { date: new Date(p.createdAt).toLocaleDateString() })}
                    {p.lastUsedAt
                      ? ` · ${t('Last used {date}', { date: new Date(p.lastUsedAt).toLocaleDateString() })}`
                      : ` · ${t('Never used')}`}
                  </span>
                </span>
                <Button variant="ghost" size="sm" onClick={() => setRemoving(p)}>
                  <Icon.Trash size={14} />
                  {t('Remove')}
                </Button>
              </li>
            ))}
          </ul>
        )}

        {supported && (
          <div className="acct-form__actions">
            <Button variant="secondary" loading={adding} onClick={addPasskey}>
              <Icon.Fingerprint size={16} />
              {t('Add passkey')}
            </Button>
          </div>
        )}
      </Card.Body>

      <ConfirmDialog
        open={!!removing}
        onCancel={() => setRemoving(null)}
        onConfirm={removePasskey}
        loading={removeBusy}
        tone="danger"
        title={t('Remove passkey?')}
        description={removing ? t('"{name}" will no longer work for signing in.', { name: removing.name }) : ''}
        confirmLabel={t('Remove')}
      />
    </Card>
  );
}
